<template>
	<ui-box new-forms>
		<form-title title="Added Places">
			Manage the places that have been added to your pass.
		</form-title>

		<form-divider inner/>

		<v-row class="mt-n2 mt-md-n4">
			<v-col>

				<!-- PlacesTable -->
				<bw-table
						ref="placesTable"
						v-model="selectedPlaces"
						aria-label="data-table"
						:custom-search="customFilterFunction()" 
						:headers="computedPlaceHeaders"
						:hide-default-footer="!localPlaces.length"
						:hide-default-header="!localPlaces.length"
						:hide-reset-row="allowSorting"
						:hide-search="!localPlaces.length"
						id="product-req-table"
						:items="localPlaces"
						:disable-sort="$vuetify.breakpoint.smAndDown"
						:row-select="showSelection"
				>
					<template v-slot:no-data>
						<div
								class="align-center d-flex justify-center w-100"
								style="height: 47px;"
						>
							Oops, no places to go yet!
						</div>
					</template>
					<template v-slot:item.data-table-select="{ isSelected, select, item }">
						<v-checkbox
								v-if="isRowSelectable(item)"
								:input-value="isSelected"
								@click="select(!isSelected)"
						></v-checkbox>
					</template>
					<template v-slot:header.data-table-select="{ props }">
						<v-checkbox
								:input-value="areAllSelectableRowsSelected"
								:indeterminate="areSomeSelectableRowsSelected"
								@change="toggleSelectableRows"
						></v-checkbox>
						
					</template>
					<template  v-if="allowSorting" v-slot:actionArea>
						<v-select
								v-if="!sortLock"
								v-model="sortMethod"
								class="rounded-lg mr-4"
								dense
								hide-details
								:items="sortMethods"
								item-text="name"
								outlined
								@change="onSortMethodChange"
						></v-select>
						<v-btn
								class="rounded-lg"
								:color="sortLock ? 'white' : 'warning lighten-1'"
								dark
								depressed
								:small="$vuetify.breakpoint.smAndDown"
								@click="toggleSortLock"
								v-ripple="{ class: 'warning--text text--lighten-1' }"
						>
							<v-icon
									v-if="sortLock"
									:class="{
										'me-2': $vuetify.breakpoint.smAndUp,
										'ms-n1': $vuetify.breakpoint.smAndUp
									}"
									color="warning lighten-1"
							>
								mdi-lock-outline
							</v-icon>
							<v-icon
									v-else
									:class="{
										'me-2': $vuetify.breakpoint.smAndUp,
										'ms-n1': $vuetify.breakpoint.smAndUp
									}"
									color="white"
							>
								mdi-lock-open-outline
							</v-icon>
							<span v-if="sortLock" class="warning--text text--lighten-1">Sort Order</span>
							<span v-else class="white--text">Save Sort Order</span>
						</v-btn>
						<v-btn
							v-if="showDownloadQrCodes"
							class="ml-3 rounded-lg"
							color="success"
							depressed
							:disabled="downloadPinNumbersLock || (downloadQrCodesLock && selectedPlaces.length === 0)"
							:loading="isDownloading"
							:outlined="!downloadQrCodesLock"
							:small="$vuetify.breakpoint.smAndDown"
							@click="onQrCodesDownload"
							v-ripple="{ class: 'text--lighten-1' }"
						>
							<v-icon left
								:class="{
										'me-2': $vuetify.breakpoint.smAndUp,
										'ms-n1': $vuetify.breakpoint.smAndUp
									}"
							>
								mdi-download
							</v-icon>
							<span class="text--lighten-1">{{downloadQrCodesLabel}}</span>
						</v-btn>
						<v-btn
							v-if="showDownloadPinCodes"
							class="ml-3 rounded-lg"
							color="success"
							depressed
							:disabled="downloadQrCodesLock || (downloadPinNumbersLock && selectedPlaces.length === 0)"
							:loading="isDownloading"
							:outlined="!downloadPinNumbersLock"
							:small="$vuetify.breakpoint.smAndDown"
							@click="onPinNumbersDownload"
							v-ripple="{ class: 'text--lighten-1' }"
						>
							<v-icon left
									:class="{
										'me-2': $vuetify.breakpoint.smAndUp,
										'ms-n1': $vuetify.breakpoint.smAndUp
									}"
							>
								mdi-download
							</v-icon>
							<span class="text--lighten-1">{{downloadPinNumbersLabel}}</span>
						</v-btn>
					</template>
					<template v-if="!sortLock && sortMethod === 'custom'" v-slot:alternativeDataTable>
						<v-row
								class="ma-0 align-center font-weight-bold caption text--secondary"
								no-gutters
								style="height: 48px;"
						>
							<v-col cols="auto" style="min-width: 48px;">
							</v-col>
							<v-col cols="auto" style="min-width: 120px;">
								<span>
									{{ placesHeaders[0].text }}
								</span>
							</v-col>
							<v-col
									class="d-flex ms-4 flex-grow-1"
									cols="auto"
							>
								<span>
									{{ placesHeaders[1].text }}
								</span>
							</v-col>
							<v-col
									cols="3"
									style="min-width: 50px;"
							>
								<span>
									{{ placesHeaders[2].text }}
								</span>
							</v-col>
						</v-row>
						<v-divider class="mb-4 opacity-70"></v-divider>
						<draggable
								v-model="localPlaces"
								class="my-n4 sortables"
								direction="vertical"
								filter="input"
								tag="div"
						>
							<transition-group
									type="transition"
							>
								<v-row
										v-for="(item) in localPlaces"
										:key="`draggable-place-${item.id}`"
										class="ma-0 align-center"
										no-gutters
								>
									<v-col cols="auto" style="min-width: 30px;">
										<v-icon
												:style="{
													marginTop: $vuetify.breakpoint.smAndDown ? 7 : 16
												}"
										>
											mdi-drag-vertical
										</v-icon>
									</v-col>
									<v-col cols="auto">
										<v-img
												aspect-ratio="2"
												cover
												class="rounded"
												:key="`place-img-${item.id}`"
												lazy-src="https://placehold.co/400x300?text=Loading"
												max-width="120"
												:src="item.image?.url || item.media[0].url || '/images/placehold.co.png'"
												:width="$vuetify.breakpoint.smAndUp ? '10vw' : '20vw'"
										></v-img>
									</v-col>
									<v-col
											class="ms-4"
											cols="auto"
									>
										<template v-if="pass?.id">
											<div class="text-subtitle-2">
												{{ item?.overwrittenNames?.length > 0 ? item.overwrittenNames[0] : item.name }}
											</div>
											<div v-if="item?.overwrittenNames?.length > 0" class="text-subtitle-3 font-italic">
												{{ item.name }}
											</div>
										</template>
										<template v-else>
											<div class="text-subtitle-2">
												{{ item.name }}
											</div>
											<div v-if="item?.overwrittenNames?.length > 0" class="text-subtitle-3 font-italic">
												<div v-for="(overwrittenName) in item.overwrittenNames" :key="`overwritten-name-${overwrittenName}`">
													{{ overwrittenName }}
												</div>
											</div>
										</template>
										<div class="grey--text mb-2 text-body-2 text--darken-1">
											{{ item.formatted_address }}
										</div>
									</v-col>
									<v-col
											class="d-flex justify-end"
											cols="auto"
											style="min-width: 50px;"
									>
										<div class="grey--text text-body-2 text--darken-1">
											{{ validationDesc(item.checkIn?.redemption_validation) || '' }}
										</div>
									</v-col>
								</v-row>
							</transition-group>
						</draggable>
					</template>
					<template v-slot:item.media="{ item, index }">
						<v-img
								aspect-ratio="2"
								cover
								class="rounded"
								:key="`place-img-${item.id}`"
								lazy-src="https://placehold.co/400x300?text=Loading"
								max-width="120"
								:src="item.image?.url || item.media[0].url || '/images/placehold.co.png'"
								:width="$vuetify.breakpoint.smAndUp ? '10vw' : '20vw'"
						></v-img>
					</template>
					<template v-slot:item.name="{ item }">
						<template v-if="pass?.id">
							<div class="text-subtitle-2">
								{{ item?.overwrittenNames?.length > 0 ? item.overwrittenNames[0] : item.name }}
							</div>
							<div v-if="item?.overwrittenNames?.length > 0" class="text-subtitle-3 font-italic">
								{{ item.name }}
							</div>
						</template>
						<template v-else>
							<div class="text-subtitle-2">
								{{ item.name }}
							</div>
							<div v-if="item?.overwrittenNames?.length > 0" class="text-subtitle-3 font-italic">
								<div v-for="(overwrittenName) in item.overwrittenNames" :key="`overwritten-name-${overwrittenName}`">
									{{ overwrittenName }}
								</div>
							</div>
						</template>
						<div class="grey--text mb-2 text-body-2 text--darken-1">
							{{ item.formatted_address }}
						</div>
					</template>
					<template v-slot:item.checkIn="{ item }">
						<div class="grey--text text-body-2 text--darken-1">
							{{ validationDesc(item.checkIn?.redemption_validation) || '' }}
						</div>
					</template>
					<template v-slot:item.actions="{ item, index }">
						<div class="d-flex justify-end">
							<v-btn
									color="secondary"
									depressed
									fab
									outlined
									small
									@click="$emit('edit-place', { place: item })"
							>
								<v-icon>
									mdi-pencil-outline
								</v-icon>
							</v-btn>
							<v-divider
									class="mx-2 my-1"
									vertical
							></v-divider>
							<v-btn
									color="error"
									depressed
									fab
									outlined
									small
									@click="$emit('remove-place', { place: item, idx: index })"
							>
								<v-icon>
									mdi-trash-can-outline
								</v-icon>
							</v-btn>
						</div>
					</template>
				</bw-table>
				<!-- End: Places Table -->
				<bw-modal
					v-if="showDownloadQrCodesModal"
					title="Download QR Codes"
				>
					<template #body>
						<div><strong>Image Format</strong></div>
						<div><v-checkbox v-model="includePlaceName" label="Include Place name on QR images"></v-checkbox></div>
					</template>
					<template #footer>
						<div class="align-center d-flex justify-space-between w-100">
							<div></div>
							<div class="d-flex justify-end">
								<v-btn
									class="me-2 me-md-4 rounded-lg"
									color="grey darken-1"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									:loading="isDownloading"
									text
									@click="showDownloadQrCodesModal = false;"
								>
									Cancel
								</v-btn>
								<v-btn
									class="rounded-lg"
									color="primary"
									depressed
									:large="$vuetify.breakpoint.mdAndUp"
									:loading="isDownloading"
									@click="doQrCodesDownload"
								>
									Download Zip
								</v-btn>
							</div>
						</div>
					</template>
					
				</bw-modal>
			</v-col>
		</v-row>

	</ui-box>
</template>

<script>
import {kebabCase} from 'lodash';
import { BwPlace, Place } from '../../helpers';
import { searchHelpers } from '../../searchHelpers';

export default {
	name: "AddedPlaces",
	props: {
		allowSorting: {
			type: Boolean,
			default: false
		},
		pass: {
			type: Object,
			default: () => ({})
		},
		partner: {
			type: Object,
			default: () => ({})
		},
		places: {
			type: Array,
			default: () => []
		}
	},
	data() {
		return {
			downloadQrCodesLock: false,
			downloadPinNumbersLock: false,
			formIsValid: {
				places: false
			},
			includePlaceName: true,
			isDownloading: false,
			localPlaces: [],
			placesHeaders: [
				{
					align: 'start',
					filterable: false,
					mandatory: true,
					text: 'Image',
					value: 'media',
					sortable: false,
					width: '1%'
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Name/Address',
					value: 'name',
					multiselect: true,
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Check-In Validation',
					value: 'checkIn',
					multiselect: true,
				},
				{
					align: 'start',
					filterable: false,
					mandatory: true,
					text: 'Actions',
					sortable: false,
					value: 'actions',
					width: '1%'
				}
			],
			selectedPlaces: [],
			showDownloadQrCodesModal: false,
			sortLock: true,
			sortMethod: 'custom',
			sortMethods: [
				{
					name: 'Custom Order',
					value: 'custom'
				},
				{
					name: 'Alphabetically',
					value: 'abc'
				}
			],

		};
	},
	created() {
		this.updateLocalPlaces();
	},
	computed: {
		areAllSelectableRowsSelected() {
			if(this.downloadQrCodesLock) {
				return this.localPlaces.filter(place => place.qrFlag).length == this.selectedPlaces.length;
			}
			if(this.downloadPinNumbersLock) {
				return this.localPlaces.filter(place => place.pinFlag).length == this.selectedPlaces.length;
			}
			return false;
		},
		areSomeSelectableRowsSelected() {
			if(this.downloadQrCodesLock) {
				return this.selectedPlaces.length > 0 && this.localPlaces.filter(place => place.qrFlag).length > this.selectedPlaces.length;
			}
			if(this.downloadPinNumbersLock) {
				return this.selectedPlaces.length > 0 &&  this.localPlaces.filter(place => place.pinFlag).length > this.selectedPlaces.length;
			}
			return false;
		},

		computedPlaceHeaders() {
			return this.placesHeaders.filter(header => {
				const hide = !this.pass?.id || this.shouldHideCheckIns;
				return !(hide && header.value === 'checkIn');
			});
		},
		downloadQrCodesLabel() {
			return 'DOWNLOAD' + (this.downloadQrCodesLock ? ' ('+this.selectedPlaces.length+') ' : ' ') + 'QR CODES';
		},
		downloadPinNumbersLabel() {
			return 'DOWNLOAD' + (this.downloadPinNumbersLock ? ' ('+this.selectedPlaces.length+') ' : ' ') + 'PIN NUMBERS';
		},
		shouldHideCheckIns() {
			return !!this.pass?.template?.settings?.check_in_validation?.hide;
		},
		showDownloadQrCodes() {
			return this.localPlaces.some(place => place.qrFlag)
		},
		showDownloadPinCodes() {
      return this.localPlaces.some(place => place.pinFlag)
			//return this.localPlaces.some(place => (place.checkIn?.redemption_validation === 'pin' || place.checkIn?.redemption_validation === 'gpsOrPin'))
		},
		
		showSelection() {
			return this.downloadQrCodesLock || this.downloadPinNumbersLock;
		},
	},
	methods: {
		kebabCase,
		async onDownload(url, fileName, type) {
			try {
				const venueIds = this.selectedPlaces.map(place => place.id);
				this.isDownloading = true;
				const response = await axios.post(url, {
					params: {
						venues: venueIds,
						includePlaceName: this.includePlaceName,
					}
				}, {
					responseType: 'blob' 
				});
				if (response.data.size === 0) {
					throw new Error('Empty file!');
				}
				const blob = new Blob([response.data], { type: `application/${type}` });
				const blobUrl = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = blobUrl;
				link.setAttribute('download', fileName);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(blobUrl); 
				this.selectedPlaces = [];
				window.EventBus.$emit('system-messages', [
					{ message: `${fileName} downloaded`, messageType: 'success' }
				]);
			} catch (error) {
				console.error(error);
				window.EventBus.$emit('system-messages', [
					{ message: error.message, messageType: 'error' }
				]);
			} finally {
				this.isDownloading = false;
			}
		},
		doQrCodesDownload() {
			const fileName = `${this.kebabCase(this.pass.name)}-qr-codes.zip`;
			this.onDownload(`/partner-portal/${this.partner.id}/passes/get-venue-qr-zip/${this.pass.id}`, fileName, 'zip');
			this.downloadQrCodesLock = false;
			this.showDownloadQrCodesModal = false;
		},
		onQrCodesDownload() {
			if(!this.downloadQrCodesLock) {
				this.downloadQrCodesLock = true;
				this.selectedPlaces = [...this.localPlaces.filter(place => place.qrFlag)];
				return;
			}
			this.showDownloadQrCodesModal = true;
			
		},
		onPinNumbersDownload() {
			if(!this.downloadPinNumbersLock) {
				this.downloadPinNumbersLock = true;
				this.selectedPlaces = [
					...this.localPlaces.filter(place =>
						place.checkIn?.redemption_validation === 'pin'||
						place.checkIn?.redemption_validation === 'gpsOrPin' ||
						place.pinFlag
					)
				];
				return;
			}
			const fileName = `${this.kebabCase(this.pass.name)}-pin_numbers.csv`;
			this.onDownload(`/partner-portal/${this.partner.id}/passes/get-venue-pin-csv/${this.pass.id}`, fileName, 'csv');
			this.downloadPinNumbersLock = false;
		},
		
		onSortMethodChange(method) {
			if (method === 'abc') {
				this.sortAlphabetically();
			}
		},
		customFilterFunction() {
			return searchHelpers.searchFieldsAndAdditionals(this.placesHeaders, ['overwrittenNames']);
		},
		isRowSelectable(row) {
			return (this.downloadQrCodesLock && row.qrFlag) || (this.downloadPinNumbersLock && row.pinFlag);
		},
		sortAlphabetically() {
			this.localPlaces.sort(
				(a, b) => {
					const aName = a.overwrittenNames.length > 0 ? a.overwrittenNames[0] : a.name;
					const bName = b.overwrittenNames.length > 0 ? b.overwrittenNames[0] : b.name;
					return aName.localeCompare(bName);
				}
			);
		},
		toggleSortLock(){
			this.sortLock = !this.sortLock;
			if (this.sortLock) {
				this.updatePlacesSortOrder();
				this.$emit('save-sort', this.localPlaces);
			}
		},
		updatePlacesSortOrder() {
			this.localPlaces.map(
				(place, index) => {
					place.deal_venue.sort_order = index;
				}
			);
		},
		toggleSelectableRows(props) {
			const selectableItems = this.localPlaces.filter(item => this.isRowSelectable(item));
			if (this.areAllSelectableRowsSelected) {
				this.selectedPlaces = this.selectedPlaces.filter(row => !selectableItems.includes(row));
			} else {
				this.selectedPlaces = [...new Set([...this.selectedPlaces, ...selectableItems])];
			}

			/*if(this.downloadQrCodesLock) {
				return this.localPlaces.filter(place => place.qrFlag).length == this.selectedPlaces.length;
			}
			if(this.downloadPinNumbersLock) {
				return this.localPlaces.filter(place => place.checkIn?.redemption_validation === 'pin' || place.checkIn?.redemption_validation === 'gpsOrPin').length == this.selectedPlaces.length;
			}
			return false;*/
			
		},
		updateLocalPlaces() {
			const places = cloneDeep(this.places);
			this.localPlaces = places.map(place => new Place(place));
		},
		validationDesc(code) {
			switch (code) {
				case 'pin':
					return 'Pin';
				case 'gps':
					return 'GPS';
				case 'gpsOrPin':
					return 'GPS or Pin';
				case 'qr':
					return 'QR Code';
				case '':
					return 'None';
				default:
					return '';
			}
		},
	},
	watch: {
		places() {
			this.updateLocalPlaces();
		}
	}
}
</script>